import { useState, useCallback, useMemo, memo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
    Box, TextField, Button, Typography, InputAdornment, Divider, IconButton,
    Container,
    Avatar,
    CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { showSnackbar } from "../../../redux/reducer/snackbarSlice";
import { ResetPasswordService } from "../../../utils/services/auth.service";
import { NAVIGATE_AUTH } from "../../../constant";
import { BusinessOutlined } from "@mui/icons-material";
import { ResetPasswordRequest } from "../../../utils/dto/request";

type SubData = {
    value: string;
    error: string;
}

type InitalData = {
    companyCode: SubData;
}

const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const ResetPasswordForm = memo(() => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [initialData, setInitialData] = useState<InitalData>({
        companyCode: {
            value: '',
            error: '',
        },
    });

    const validateInputs = useCallback(() => {
        if (!token) {
            setError("Invalid or missing token.");
            return false;
        }
        if (!PASSWORD_REGEX.test(newPassword)) {
            setError("Password must be at least 8 characters long, contain a number, and a special character.");
            return false;
        }
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return false;
        }
        if (!initialData.companyCode.value) {
            const errorMsg = "Company Code is required.";
            setInitialData(prev => ({
                ...prev,
                companyCode: {
                    value: prev.companyCode.value,
                    error: errorMsg,
                },
            }));
            return false;
        }        
        return true;
    }, [token, newPassword, confirmPassword]);

    const handleSubmit = useCallback(async (event: React.FormEvent) => {
        event.preventDefault();
        setError("");
        if (!validateInputs()) return;

        setLoading(true);
        try {
            const payload: ResetPasswordRequest = { token, new_password: newPassword, company_code: initialData.companyCode.value };
            const { code, message } = await ResetPasswordService(payload);
            setLoading(false);

            if (code === 200) {
                dispatch(showSnackbar({ type: "success", message }));
                setSuccess(true);
                setNewPassword("");
                setConfirmPassword("");
            } else {
                setError(message || "Failed to reset password.");
            }
        } catch {
            setLoading(false);
            setError("An error occurred. Please try again.");
        }
    }, [dispatch, token, newPassword, validateInputs]);

    const handleBackToLogin = useCallback(() => {
        navigate(NAVIGATE_AUTH.LOGIN_PAGE);
    }, [navigate]);

    const handleCompanyCodeChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value.toUpperCase().trim().slice(0, 6);
            const cleaned = raw.replace(/[^A-Z0-9]/g, '');

            let errorMsg = '';
            if (!cleaned) {
                errorMsg = 'Company code is required.';
            } else if (cleaned.length < 6) {
                errorMsg = 'Company code must be 6 characters.';
            }

            setInitialData(prev => ({
                ...prev,
                companyCode: {
                    value: cleaned,
                    error: errorMsg,
                },
            }));
        },
        []
    );

    const inputProps = useMemo(() => ({
        newPassword: {
            startAdornment: (
                <InputAdornment position="end">
                    <LockOutlinedIcon fontSize="small" sx={{ mr: 1 }} color="primary" />
                    <Divider orientation="vertical" sx={{ height: 15 }} />
                </InputAdornment>
            ),
            endAdornment: (
                <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} aria-label="Toggle password visibility">
                        {showPassword ? <VisibilityOutlinedIcon fontSize="small" color="primary" /> : <VisibilityOffOutlinedIcon fontSize="small" color="primary" />}
                    </IconButton>
                </InputAdornment>
            ),
        },
        confirmPassword: {
            startAdornment: (
                <InputAdornment position="end">
                    <LockOutlinedIcon fontSize="small" sx={{ mr: 1 }} color="primary" />
                    <Divider orientation="vertical" sx={{ height: 15 }} />
                </InputAdornment>
            ),
            endAdornment: (
                <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} aria-label="Toggle confirm password visibility">
                        {showConfirmPassword ? <VisibilityOutlinedIcon fontSize="small" color="primary" /> : <VisibilityOffOutlinedIcon fontSize="small" color="primary" />}
                    </IconButton>
                </InputAdornment>
            ),
        },
    }), [showPassword, showConfirmPassword]);

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: 'url(/images/login_background.png)',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                zIndex: -1,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(15, 24, 80, 0.34)',
                    zIndex: -1,
                },
            }}
        >
            <Container
                component="main"
                maxWidth="xs"
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        p: 3,
                        borderRadius: 3,
                        bgcolor: '#fff',
                        boxShadow: "0px 8px 16px rgba(0,0,0,0.1)",
                        my: 2
                    }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Avatar sx={{ bgcolor: "primary.main", width: 64, height: 64, mb: 2, boxShadow: "0px 4px 10px rgba(0,0,0,0.1)" }}>
                            <LockOutlinedIcon fontSize="large" />
                        </Avatar>
                        <Typography component="h1" variant="h5" fontWeight={700} textAlign="center" color="text.primary">
                            Reset Password
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2, textAlign: "center", fontWeight: 500 }}>
                            Enter new password
                        </Typography>
                    </Box>
                    <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto', p: 2 }}>
                        {success ? (
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" color="text.primary" sx={{ mb: 2 }}>
                                    Password Reset Successfully!
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Your password has been updated. You can now log in with your new password.
                                </Typography>
                                <Button
                                    variant="text"
                                    onClick={handleBackToLogin}
                                    sx={{
                                        fontStyle: 'italic' as const,
                                        textTransform: 'none' as const,
                                        color: 'primary.main',
                                    }}
                                    aria-label="Back to login"
                                >
                                    Back to Login
                                </Button>
                            </Box>
                        ) : (
                            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    margin="normal"
                                    fullWidth
                                    placeholder="Enter company code"
                                    label={<>Company Code <span style={{ color: "red" }}>*</span></>}
                                    value={initialData.companyCode.value}
                                    onChange={handleCompanyCodeChange}
                                    error={!!initialData.companyCode.error}
                                    helperText={initialData.companyCode.error}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="end">
                                                <BusinessOutlined fontSize="small" sx={{ mr: 1 }} color="primary" />
                                                <Divider orientation="vertical" sx={{ height: 15 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{
                                        maxLength: 6,
                                        pattern: "[A-Z0-9]*",
                                    }}
                                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />

                                <TextField
                                    variant="outlined"
                                    size="small"
                                    margin="normal"
                                    fullWidth
                                    placeholder="Enter new password"
                                    label={<>New Password <span style={{ color: "red" }}>*</span></>}
                                    type={showPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={inputProps.newPassword}
                                    sx={{
                                        "& .MuiOutlinedInput-root": { borderRadius: 2 },
                                    }}
                                    disabled={loading}
                                />
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    margin="normal"
                                    fullWidth
                                    placeholder="Confirm new password"
                                    label={<>Confirm Password <span style={{ color: "red" }}>*</span></>}
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={inputProps.confirmPassword}
                                    sx={{
                                        mb: 2,
                                        "& .MuiOutlinedInput-root": { borderRadius: 2 },
                                    }}
                                    disabled={loading}
                                />
                                {error && (
                                    <Typography variant="body2" color="error" textAlign="center" sx={{ mb: 1 }}>
                                        {error}
                                    </Typography>
                                )}
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    sx={{
                                        mt: 2,
                                        py: 1.2,
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        boxShadow: "0px 4px 12px rgba(33, 150, 243, 0.3)",
                                    }}
                                    disabled={loading}
                                    aria-label="Reset password"
                                >
                                    {loading ?
                                        <>
                                            Resetting...
                                            <CircularProgress size={20} sx={{ color: 'primary.light', ml: 1, }} />
                                        </>
                                        : "Reset Password"}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Container >
        </Box >

    );
});

export default ResetPasswordForm;